import { Box, InputAdornment } from "@mui/material"
import type { FC } from "react"
import { useState } from "react"
import { useFieldArray, useFormContext } from "react-hook-form"

import { GOODS_RECEIPT_PALLET_ROW_LOT_EMPTY_FORM_VALUES } from "../../constants"
import type { SoftReceivedPurchaseOrdersFormValues } from "../../schemas"

import { SoftReceivedLotForm } from "./SoftReceivedLotForm"
import { SoftReceivedLotRemovalConfirmationDialog } from "./SoftReceivedLotRemovalConfirmationDialog"

import { FormNumericField } from "@/components/UiKit"
import { PermissionActionType } from "@/constants/permissions"
import type { Product, UnitOfMeasurement } from "@/graphql/codegen/graphql"
import { useAppView } from "@/hooks/useAppView"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { classNames } from "@/utils/utils"

interface SoftReceivedPalletRowFormProps {
  purchaseOrderIndex: number
  purchaseOrderLineIndex: number
  palletRowIndex: number
  uom: UnitOfMeasurement | undefined
  product: Product
  isResettingRef?: React.MutableRefObject<boolean>
  onRemove: () => void
}

export const SoftReceivedPalletRowForm: FC<SoftReceivedPalletRowFormProps> = ({
  purchaseOrderIndex,
  purchaseOrderLineIndex,
  palletRowIndex,
  uom,
  product,
  isResettingRef,
  onRemove: onRemovePallet,
}) => {
  const { hasPermissions } = usePermissionManager()
  const { control } = useFormContext<SoftReceivedPurchaseOrdersFormValues>()
  const [lotToRemove, setLotToRemove] = useState<number | null>(null)
  const { isTabletView } = useAppView()
  const canMaintain = hasPermissions([PermissionActionType.WAREHOUSE_RECEIVING_MAINTENANCE])

  const baseFieldPath =
    `purchaseOrders.${purchaseOrderIndex}.lines.${purchaseOrderLineIndex}.palletRows.${palletRowIndex}` as const
  const fieldPaths = {
    palletUnitQuantity: `${baseFieldPath}.palletUnitQuantity` as const,
    palletsCount: `${baseFieldPath}.palletsCount` as const,
    lots: `${baseFieldPath}.lots` as const,
  }

  const {
    fields: lots,
    append: appendLot,
    remove: removeLot,
  } = useFieldArray({
    control,
    name: fieldPaths.lots,
  })

  const handleLotAdd = () => {
    appendLot({
      ...GOODS_RECEIPT_PALLET_ROW_LOT_EMPTY_FORM_VALUES,
      packageUnitQuantity: product.packageUsWeight ?? null,
      packagingType: product.packagingType ?? null,
    })
  }

  const handleLotRemove = (index: number) => {
    setLotToRemove(index)
  }

  const handleLotRemoveConfirm = () => {
    if (lotToRemove === null) return

    if (lots.length === 1) {
      onRemovePallet()
    } else {
      removeLot(lotToRemove)
    }

    setLotToRemove(null)
  }

  const handleLotRemoveCancel = () => {
    setLotToRemove(null)
  }

  return (
    <>
      <Box className={classNames("grid gap-x-4", { "grid-cols-8": !isTabletView })}>
        <Box
          className={classNames("flex gap-x-4", { "col-span-2": !isTabletView, "pb-6": isTabletView })}
          data-testid='pallet-row-form'
        >
          <FormNumericField
            control={control}
            name={fieldPaths.palletUnitQuantity}
            label='Pallet UoM'
            required
            allowNegative={false}
            decimalScale={2}
            className='flex-1'
            disabled={!canMaintain}
            slotProps={{ input: { endAdornment: <InputAdornment position='end'>{uom?.name}</InputAdornment> } }}
            data-testid='quantity-per-pallet-field'
          />
          <FormNumericField
            control={control}
            name={fieldPaths.palletsCount}
            label='Pallet Qty'
            required
            allowNegative={false}
            decimalScale={0}
            className='flex-1'
            disabled={!canMaintain}
            data-testid='pallets-count-field'
          />
        </Box>

        <Box className={classNames("flex flex-col gap-y-6", { "col-span-6": !isTabletView, "pl-12": isTabletView })}>
          {lots.map((lot, lotIndex) => (
            <SoftReceivedLotForm
              key={lot.id}
              baseFieldPath={`${fieldPaths.lots}.${lotIndex}`}
              product={product}
              uom={uom}
              isLast={lotIndex === lots.length - 1}
              canMaintain={canMaintain}
              isResettingRef={isResettingRef}
              onAdd={handleLotAdd}
              onRemove={() => handleLotRemove(lotIndex)}
            />
          ))}
        </Box>
      </Box>

      {lotToRemove !== null && (
        <SoftReceivedLotRemovalConfirmationDialog onClose={handleLotRemoveCancel} onConfirm={handleLotRemoveConfirm} />
      )}
    </>
  )
}
